import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

/**
 * Soft, friendly upgrade nudge for free-tier users on the dashboard.
 * Sells VALUE in a positive tone — never sells with anxiety.
 *
 * Props:
 *  - testid:  data-testid prefix
 *  - title:   override title (defaults to i18n)
 *  - bullets: override the value-prop bullet array (defaults to i18n)
 */
export default function UpgradeNudge({
  testid = "upgrade-nudge",
  title,
  bullets,
}) {
  const t = useT();
  const items = bullets || [
    t("upgrade.nudge.bullets.tasks"),
    t("upgrade.nudge.bullets.analysis"),
    t("upgrade.nudge.bullets.content"),
    t("upgrade.nudge.bullets.rescan"),
  ];
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.07] via-purple-500/[0.04] to-transparent p-6 sm:p-7"
      data-testid={testid}
    >
      <div className="absolute -top-24 -right-20 w-60 h-60 bg-cyan-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-24 -left-20 w-60 h-60 bg-purple-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="shrink-0">
          <div className="w-12 h-12 rounded-2xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Sparkles size={20} className="text-cyan-300" strokeWidth={1.6} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 mb-1.5">
            {t("upgrade.nudge.eyebrow")}
          </div>
          <h3 className="font-display text-lg sm:text-xl font-semibold text-white mb-1.5">
            {title || t("upgrade.nudge.title")}
          </h3>
          <p className="text-sm text-slate-300 leading-relaxed mb-3">
            {t("upgrade.nudge.subtitle")}
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {items.map((b, i) => (
              <li key={i} className="text-sm text-slate-200 flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
                <span className="leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="shrink-0 sm:self-center w-full sm:w-auto">
          <Link
            to="/app/planlar"
            className="neon-btn rounded-xl px-5 py-2.5 text-sm text-white inline-flex items-center gap-2 font-medium w-full sm:w-auto justify-center"
            data-testid={`${testid}-cta`}
          >
            {t("upgrade.nudge.cta")} <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
